import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ISettlements } from '@/modules/Settlements/types';
import { IReconciliation } from '@/modules/workingDayReconciliation/types';
import axiosClient from '@/shared/lib/axios';
import { IGeneral } from '@/shared/types';

// ************************************ settlements *************************************
export const getWorkerSettlements = async (
  params: ISettlements.IGetWorkerSettlementsSchema
): Promise<ISettlements.IGetWorkerSettlementsResponse> => {
  const { data } = await axiosClient.get(
    'company/portalInsights/worker-settlements',
    {
      params: params,
    }
  );
  return data;
};
export const useGetWorkerSettlements = (
  params: ISettlements.IGetWorkerSettlementsSchema
) => {
  return useQuery({
    queryKey: ['getWorkerSettlements', params],
    queryFn: () => getWorkerSettlements(params),
    enabled: !!params.entityId && !!params.endDate && !!params.startDate,
  });
};
export const createWorkerSettlements = async (
  data: ISettlements.ICreateWorkerSettlementSchema
) => {
  const response = await axiosClient.post(
    '/company/portalInsights/worker-settlements',
    {
      ...data,
    }
  );
  return response;
};
export const useCreateWorkerSettlements = () => {
  return useMutation({
    mutationKey: ['createWorkerSettlement'],
    mutationFn: createWorkerSettlements,
  });
};
export const useCreateCrewSettlements = () => {
  return useMutation((data: ISettlements.ICreateSettlementSchema) =>
    axiosClient.post('/company/portalInsights/settlements', {
      ...data,
    })
  );
};

// ************************************ crops *************************************
export const getCrops = async (
  params?: ISettlements.IGetCropsSchema
): Promise<ISettlements.ICropItem[]> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/settlements/settlement-crops',
    {
      params,
    }
  );
  return data;
};
export const useGetSettlementCrops = (params: ISettlements.IGetCropsSchema) => {
  return useMutation(['settlement-crops', params], () => getCrops(params));
};

// ************************************ suppliers *************************************
export const getSettlementSuppliers = async (
  params: ISettlements.IGetSuppliersSchema
): Promise<ISettlements.ISupplierItem[]> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/settlements/suppliers',
    {
      params: params,
    }
  );
  return data;
};
export const useGetSettlementSuppliers = (
  params: ISettlements.IGetSuppliersSchema
) => {
  return useQuery(
    ['settlements-suppliers', params],
    () => getSettlementSuppliers(params),
    {
      keepPreviousData: true,
    }
  );
};
// ************************************ workers *************************************
export const getSettlementWorkers = async (
  params: ISettlements.ISearchWorkerSettlementSchema
) => {
  const { data } =
    await axiosClient.get<IReconciliation.IGetWorkersDailyDuesDetails>(
      '/company/portalInsights/worker-settlements/workers',
      {
        params: params,
      }
    );
  return {
    items: data.workers,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.totalCount / params.pageSize),
    totalItems: data.totalCount,
    nextPage:
      params?.pageNumber * params?.pageSize < data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};
export const useGetSettlementWorkers = (
  params: ISettlements.ISearchWorkerSettlementSchema
) => {
  return useInfiniteQuery({
    queryKey: ['settlements-workers', params],
    queryFn: ({ pageParam }) =>
      getSettlementWorkers({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
};

// **************************************** list *********************************************
export const getSettlements = async (
  params: ISettlements.IGetSettlementsSchema
): Promise<ISettlements.IGetSettlements> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/settlements',
    {
      params: params,
    }
  );
  return data;
};
export const useGetSettlements = (
  params: ISettlements.IGetSettlementsSchema
) => {
  return useQuery(['settlements', params], () => getSettlements(params), {
    enabled:
      Boolean(params.entityId === 0 ? true : params.entityId) &&
      Boolean(params.startDate && params.endDate),
  });
};

// ***************************************** Crew Settlements details ***************************

export const getSettlementDetails = async ({
  id,
  entityId,
}: ISettlements.IGetSettlementDetailsSchema): Promise<ISettlements.IGetSettlementDetails> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/settlements/${entityId}/${id}`
  );

  return data;
};
export const useGetSettlementDetails = (
  params: ISettlements.IGetSettlementDetailsSchema
) => {
  return useQuery(
    ['settlementDetails', params],
    () => getSettlementDetails(params),
    {
      enabled:
        Boolean(params?.entityId === 0 ? true : params?.entityId) &&
        Boolean(params?.id?.toString() === '0' ? true : params?.id),
    }
  );
};
// ***************************************** Worker Settlements details ***************************

export const getWorkerSettlementDetails = async ({
  id,
  entityId,
}: ISettlements.IGetSettlementDetailsSchema): Promise<ISettlements.IGetWorkerSettlementDetails> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/worker-settlements/${entityId}/${id}`
  );

  return data;
};
export const useGetWorkerSettlementDetails = (
  params: ISettlements.IGetSettlementDetailsSchema
) => {
  return useQuery(
    ['workerSettlementDetails', params],
    () => getWorkerSettlementDetails(params),
    {
      enabled:
        Boolean(params?.entityId === 0 ? true : params?.entityId) &&
        Boolean(params?.id?.toString() === '0' ? true : params?.id),
    }
  );
};

/*********************************** PDF ***************************************** */
export const getSettlementDetailsPrint = async <
  T extends IGeneral.ISettlementLevel
>({
  id,
  entityId,
  type,
}: ISettlements.IGetSettlementDetailsSchema): Promise<
  T extends 2
    ? ISettlements.IGetWorkerSettlementDetails
    : ISettlements.IGetSettlementDetails
> => {
  const { data } = await axiosClient.get(
    `${
      type === 2
        ? '/company/portalInsights/worker-settlements/print/'
        : '/company/portalInsights/settlements/print/'
    }${entityId}/${id}`
  );

  return data;
};
export const useGetSettlementDetailsPrint = (
  params: ISettlements.IGetSettlementDetailsSchema
) => {
  return useQuery(
    ['settlementDetailsPrint', params],
    () => getSettlementDetailsPrint(params),
    {
      enabled:
        Boolean(params?.entityId === 0 ? true : params?.entityId) &&
        Boolean(params?.id?.toString() === '0' ? true : params?.id),
    }
  );
};
/************************************ EXCEL ******************************************** */

export const getSettlementDetailsXLSX = async <
  T extends IGeneral.ISettlementLevel
>({
  id,
  entityId,
  type,
}: ISettlements.IGetSettlementDetailsSchema): Promise<
  T extends 2
    ? ISettlements.IGetWorkerSettlementDetails
    : ISettlements.IGetSettlementDetails
> => {
  const { data } = await axiosClient.get(
    `${
      type === 2
        ? '/company/portalInsights/worker-settlements/xlsx/'
        : '/company/portalInsights/settlements/print/'
    }${entityId}/${id}`
  );

  return data;
};
export const useGetSettlementDetailsXLSX = (
  params: ISettlements.IGetSettlementDetailsSchema
) => {
  return useQuery(
    ['settlementDetailsXLSX', params],
    () => getSettlementDetailsXLSX(params),
    {
      enabled:
        Boolean(params?.entityId === 0 ? true : params?.entityId) &&
        Boolean(params?.id?.toString() === '0' ? true : params?.id),
    }
  );
};
/************************************ ISSUE SETTLEMENT ********************************** */

export const useIssueSettlement = (type?: IGeneral.ISettlementLevel) => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: ISettlements.IIssueSettlementSchema) =>
      axiosClient.patch(
        `${
          type === 2
            ? '/company/portalInsights/worker-settlements/'
            : '/company/portalInsights/settlements/'
        }${params.entityId}/${params.id}`
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['settlementDetails']);
        queryClient.invalidateQueries(['workerSettlementDetails']);
      },
    }
  );
};
/***************************** delete action ************* */
export const useDeleteSettlement = (type?: IGeneral.ISettlementLevel) => {
  return useMutation((params: ISettlements.IDeleteSettlementSchema) =>
    axiosClient.delete(
      `${
        type === 2
          ? '/company/portalInsights/worker-settlements/'
          : '/company/portalInsights/settlements/'
      }${params.entityId}/${params.id}`
    )
  );
};
